import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Table,
  Space,
  Tooltip,
  Button,
  PageHeader,
  Tag,
  Result,
  Spin,
  Empty,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusCircleOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import {
  handleApiSuccess,
  handleApiError,
  confirmDelete,
  accessControls,
  MyBreadcrumb,
} from 'utils';
import {
  schedulingSchedulesListService,
  schedulingScheduleDeleteService,
} from 'services/scheduling';

export default function SchedulesList() {
  const history = useHistory();
  const [permissions, setPermissions] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);

  useEffect(() => {
    accessControls
      .fetch()
      .then(setPermissions)
      .catch(() => setPermissions({}));
  }, []);

  const loadData = () => {
    setIsLoading(true);
    schedulingSchedulesListService()
      .then((res) => {
        setIsLoading(false);
        setDataSource(res.data.items || []);
      })
      .catch((err) => {
        setIsLoading(false);
        handleApiError(err);
      });
  };

  useEffect(() => {
    if (permissions == null) return;
    if (!accessControls.can(permissions, 'Scheduling.read:any')) return;
    loadData();
  }, [permissions]);

  const deleteRecord = (id) => {
    confirmDelete().then(() => {
      setIsLoading(true);
      schedulingScheduleDeleteService(id)
        .then((res) => {
          setIsLoading(false);
          handleApiSuccess(res);
          loadData();
        })
        .catch((err) => {
          setIsLoading(false);
          handleApiError(err);
        });
    });
  };

  const columns = [
    {
      title: 'Schedule',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, {
          sensitivity: 'base',
        }),
      render: (text, record) => (
        <Button
          type="link"
          style={{ padding: 0, height: 'auto', fontWeight: 500 }}
          onClick={() =>
            history.push(
              '/time-tracker/scheduling/' + record._id + '/board',
            )
          }
        >
          {text}
        </Button>
      ),
    },
    {
      title: 'Timezone',
      dataIndex: 'timezone',
      key: 'timezone',
      render: (v) => v || 'Viewer default',
    },
    {
      title: 'Member groups',
      dataIndex: 'member_groups',
      key: 'member_groups',
      render: (groups) =>
        groups && groups.length ? (
          <Space wrap size={[4, 4]}>
            {groups.map((g) => (
              <Tag key={g._id || g}>{g.group_name || g}</Tag>
            ))}
          </Space>
        ) : (
          '—'
        ),
    },
    {
      title: 'Manager groups',
      dataIndex: 'manager_groups',
      key: 'manager_groups',
      render: (groups) =>
        groups && groups.length ? (
          <Space wrap size={[4, 4]}>
            {groups.map((g) => (
              <Tag key={g._id || g} color="blue">
                {g.group_name || g}
              </Tag>
            ))}
          </Space>
        ) : (
          '—'
        ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (v) => (
        <Tag color={v === 'ENABLED' ? 'green' : 'default'}>{v || '—'}</Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 160,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Open weekly board">
            <Button
              type="primary"
              shape="circle"
              size="small"
              icon={<CalendarOutlined />}
              onClick={() =>
                history.push(
                  '/time-tracker/scheduling/' + record._id + '/board',
                )
              }
            />
          </Tooltip>
          {accessControls.can(permissions, 'Scheduling.update:any') && (
            <Tooltip title="Edit">
              <Button
                shape="circle"
                size="small"
                icon={<EditOutlined />}
                onClick={() =>
                  history.push(
                    '/time-tracker/scheduling/update/' + record._id,
                  )
                }
              />
            </Tooltip>
          )}
          {accessControls.can(permissions, 'Scheduling.update:any') && (
            <Tooltip title="Delete">
              <Button
                shape="circle"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => deleteRecord(record._id)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  if (permissions == null) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!accessControls.can(permissions, 'Scheduling.read:any')) {
    return (
      <div style={{ padding: 24 }}>
        <Result
          status="403"
          title="Access denied"
          subTitle="You do not have permission to view Scheduling."
          extra={
            <Button type="primary" onClick={() => history.push('/')}>
              Go to Dashboard
            </Button>
          }
        />
      </div>
    );
  }

  const canCreate = accessControls.can(permissions, 'Scheduling.update:any');

  return (
    <div>
      <MyBreadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Time Tracker', path: '/time-tracker/tracker' },
          { label: 'Schedules', path: '' },
        ]}
      />
      <PageHeader
        ghost={false}
        title="Schedules"
        subTitle="Weekly rosters, shift assignment, and open-slot claiming"
        extra={
          canCreate
            ? [
                <Button
                  key="activities"
                  onClick={() =>
                    history.push('/time-tracker/scheduling/activities')
                  }
                >
                  Activities
                </Button>,
                <Button
                  key="create"
                  type="primary"
                  icon={<PlusCircleOutlined />}
                  onClick={() =>
                    history.push('/time-tracker/scheduling/create')
                  }
                >
                  New schedule
                </Button>,
              ]
            : []
        }
      />
      <Table
        rowKey="_id"
        loading={isLoading}
        dataSource={dataSource}
        columns={columns}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        size="middle"
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No schedules yet"
            >
              {canCreate && (
                <Button
                  type="primary"
                  icon={<PlusCircleOutlined />}
                  onClick={() =>
                    history.push('/time-tracker/scheduling/create')
                  }
                >
                  Create your first schedule
                </Button>
              )}
            </Empty>
          ),
        }}
      />
    </div>
  );
}
